/* ============================================================
   Olivia & Mary cart drawer

   Registers <cart-drawer>, so the theme's existing product form
   (product-form.js) opens and refreshes this drawer without any
   change on its side. It expects exactly three methods:
   getSectionsToRender(), setActiveElement() and renderContents().

   Everything inside [data-ocd-contents] is server rendered, so
   after every cart change we ask Shopify for a fresh copy of the
   section instead of patching numbers in the DOM by hand.
   ============================================================ */
(function () {
  'use strict';

  if (customElements.get('cart-drawer')) return;

  var SECTION_ID = 'olivia-cart-drawer';

  class OliviaCartDrawer extends HTMLElement {
    constructor() {
      super();
      this.busy = false;
      this.refreshVersion = 0;
      this.onKeydown = this.onKeydown.bind(this);
      this.reservationDeadline = 0;
      this.reservationSync = 0;
      this.onReservationResume = () => this.syncReservation();
      this.onPageShow = (event) => {
        if (!event.persisted) return;
        this.busy = false;
        this.classList.remove('is-busy');
        this.setAttribute('aria-busy', 'false');
        this.refresh();
      };
      this.onReservationStorage = (event) => {
        if (event.key === this.reservationKey || event.key === null) this.syncReservation();
      };
      this.addEventListener('submit', (event) => {
        if (event.target.matches('[data-ocd-discount-form]')) {
          event.preventDefault();
          this.applyDiscount(event.target);
          return;
        }
        if (event.target.matches('.ocd__checkout-form') && this.reservationDeadline && Date.now() >= this.reservationDeadline) {
          event.preventDefault();
          this.tickReservation();
        }
      }, true);

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-ocd-close]') || event.target.closest('[data-ocd-overlay]')) {
          event.preventDefault();
          this.close();
        }
      });

      this.addEventListener('click', (event) => {
        var step = event.target.closest('[data-ocd-step]');
        if (step) {
          event.preventDefault();
          this.onStep(step);
          return;
        }
        var remove = event.target.closest('[data-ocd-remove]');
        if (remove) {
          event.preventDefault();
          this.setQuantity(this.lineOf(remove), 0, remove);
        }
      });

      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-ocd-qty-input]')) {
          var value = parseInt(event.target.value, 10);
          if (!Number.isFinite(value) || value < 0) value = 1;
          this.setQuantity(this.lineOf(event.target), value, event.target);
        }
        if (event.target.matches('[data-ocd-option]')) {
          this.onOptionChange(event.target);
        }
      });
    }

    connectedCallback() {
      this.syncReservation();
      this.tickReferenceTimer();
      this.reservationInterval = window.setInterval(() => { this.tickReservation(); this.tickReferenceTimer(); }, 1000);
      document.addEventListener('visibilitychange', this.onReservationResume);
      window.addEventListener('pageshow', this.onReservationResume);
      // Back from checkout: the browser restores the old page from its
      // back/forward cache, with the cart as it was before. Re-render the
      // drawer and the cart icon from Shopify, without opening the drawer.
      window.addEventListener('pageshow', this.onPageShow);
      window.addEventListener('storage', this.onReservationStorage);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event && (event.source === 'olivia-cart-drawer' || (event.cartData && event.cartData.sections && event.cartData.sections[SECTION_ID]))) return;
          this.refresh();
        });
      }
    }

    disconnectedCallback() {
      window.clearInterval(this.reservationInterval);
      document.removeEventListener('visibilitychange', this.onReservationResume);
      window.removeEventListener('pageshow', this.onReservationResume);
      window.removeEventListener('pageshow', this.onPageShow);
      window.removeEventListener('storage', this.onReservationStorage);
      this.reservationSync++;
      if (this.unsubscribe) this.unsubscribe();
      document.removeEventListener('keydown', this.onKeydown);
    }

    /* ---------- open / close ---------- */

    open(triggeredBy) {
      if (triggeredBy) this.setActiveElement(triggeredBy);
      this.classList.add('is-open');
      document.body.classList.add('overflow-hidden');
      document.addEventListener('keydown', this.onKeydown);

      var panel = this.querySelector('.ocd__panel');
      if (panel) {
        // Wait for the transition to start before moving focus, otherwise
        // Safari jumps the page to the top of the drawer.
        requestAnimationFrame(function () {
          if (typeof trapFocus === 'function') trapFocus(panel, panel.querySelector('[data-ocd-close]'));
          else panel.focus({ preventScroll: true });
        });
      }
    }

    close() {
      this.classList.remove('is-open');
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);
      document.body.classList.remove('overflow-hidden');
      document.removeEventListener('keydown', this.onKeydown);
      if (this.activeElement && document.body.contains(this.activeElement)) {
        this.activeElement.focus({ preventScroll: true });
      }
    }

    onKeydown(event) {
      if (event.key === 'Escape') this.close();
    }

    setActiveElement(element) {
      this.activeElement = element;
    }

    /* ---------- the API product-form.js calls ---------- */

    getSectionsToRender() {
      return [
        { id: SECTION_ID, selector: '[data-ocd-contents]' },
        { id: 'cart-icon-bubble', selector: '.shopify-section' },
      ];
    }

    renderContents(parsedState) {
      this.refreshVersion++;
      var sections = (parsedState && parsedState.sections) || {};
      this.getSectionsToRender().forEach((section) => {
        var html = sections[section.id];
        if (!html) return;
        this.replaceSection(section, html);
      });
      this.setEmptyState(!!this.querySelector('.ocd__empty'));
      this.syncReservation();
      this.open();
    }

    replaceSection(section, html) {
      var parsed = new DOMParser().parseFromString(html, 'text/html');
      var source = parsed.querySelector(section.selector);
      var target =
        section.id === SECTION_ID
          ? this.querySelector('[data-ocd-contents]')
          : document.getElementById('cart-icon-bubble');
      if (!source || !target) return;
      var active = document.activeElement;
      var activeRow = active && active.closest ? active.closest('.ocd__item') : null;
      var activeVariant = activeRow && activeRow.dataset.ocdVariant;
      var activeSelector = active && active.matches('[data-ocd-step]') ? '[data-ocd-step="' + active.dataset.ocdStep + '"]' :
        active && active.matches('[data-ocd-qty-input]') ? '[data-ocd-qty-input]' :
        active && active.matches('[data-ocd-option]') ? '[data-ocd-option="' + active.dataset.ocdOption + '"]' :
        active && active.matches('[data-ocd-remove]') ? '[data-ocd-remove]' : null;
      var activeId = active && target.contains(active) ? active.id : '';
      target.innerHTML = source.innerHTML;
      if (section.id === SECTION_ID) {
        var root = parsed.querySelector('cart-drawer');
        if (root) this.dataset.ocdCount = root.dataset.ocdCount;
        this.setEmptyState(!!source.querySelector('.ocd__empty'));
        this.tickReferenceTimer();
        if (this.classList.contains('is-open')) {
          var row = Array.from(this.querySelectorAll('.ocd__item')).find((element) => element.dataset.ocdVariant === activeVariant);
          var nextFocus = activeId ? document.getElementById(activeId) : row && activeSelector ? row.querySelector(activeSelector) : null;
          var panel = this.querySelector('.ocd__panel');
          if (typeof trapFocus === 'function') trapFocus(panel, nextFocus || this.querySelector('[data-ocd-close]'));
          else if (nextFocus) nextFocus.focus({ preventScroll: true });
        }
      }
    }

    setEmptyState(isEmpty) {
      this.classList.toggle('ocd--empty', !!isEmpty);
    }

    /* ---------- cart operations ---------- */

    lineOf(element) {
      var item = element.closest('.ocd__item');
      return item ? Number(item.dataset.ocdLine) : 0;
    }

    onStep(button) {
      if (this.busy) return;
      var item = button.closest('.ocd__item');
      var input = item && item.querySelector('[data-ocd-qty-input]');
      if (!input) return;
      var next = parseInt(input.value, 10) + Number(button.dataset.ocdStep);
      if (next < 0) next = 0;
      input.value = next;
      this.setQuantity(this.lineOf(button), next, button);
    }

    setBusy(state) {
      this.busy = state;
      this.classList.toggle('is-busy', state);
      this.setAttribute('aria-busy', String(state));
      this.querySelectorAll('.ocd__checkout, .ocd__discount button').forEach((button) => { button.disabled = state; });
      if (state) { this.reservationSync++; this.refreshVersion++; }
      else this.syncReservation();
    }

    setQuantity(line, quantity, origin) {
      if (!line || this.busy) return;
      this.setBusy(true);
      this.clearError(origin);
      var removed = null;
      if (quantity === 0) {
        var row = origin && origin.closest('.ocd__item') || this.querySelector('[data-ocd-line="' + line + '"]');
        var metadata = row && row.querySelector('[data-ocd-line-data]');
        try { if (metadata) removed = JSON.parse(metadata.textContent); } catch (error) {}
      }

      fetch(window.routes.cart_change_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          line: line,
          quantity: quantity,
          sections: this.getSectionsToRender().map((s) => s.id),
          sections_url: window.location.pathname,
        }),
      })
        .then((response) => response.json())
        .then((state) => {
          if (state.status) {
            this.showError(origin, state.description || state.message);
            return this.refresh().then(() => this.showError(null, state.description || state.message));
          }
          var rendered;
          if (state.sections && state.sections[SECTION_ID]) this.renderFromState(state);
          else rendered = this.refresh().then(() => this.publish(state));
          return Promise.resolve(rendered).then(() => {
            if (removed) this.dispatchEvent(new CustomEvent('ocd:line-removed', { detail: removed }));
          });
        })
        .catch(() => {
          this.showError(origin, window.cartStrings ? window.cartStrings.error : 'Something went wrong.');
        })
        .finally(() => this.setBusy(false));
    }

    /* A line's variant cannot be changed in place, so the old line is removed
       and the new variant is added with the same quantity. */
    onOptionChange(select) {
      if (this.busy) return;
      var item = select.closest('.ocd__item');
      if (!item) return;

      var line = Number(item.dataset.ocdLine);
      var selects = Array.prototype.slice.call(item.querySelectorAll('[data-ocd-option]'));
      var wanted = selects.map((el) => el.value);
      var input = item.querySelector('[data-ocd-qty-input]');
      var quantity = input ? parseInt(input.value, 10) || 1 : 1;

      var data = this.variantDataFor(item);
      if (!data) return this.refresh();

      var match = data.variants.filter(function (variant) {
        return variant.options.every(function (value, index) {
          return value === wanted[index];
        });
      })[0];

      if (!match) {
        this.showError(select, this.unavailableMessage(wanted));
        return this.refresh().then(() => this.showError(null, this.unavailableMessage(wanted)));
      }
      if (!match.available) {
        this.showError(select, this.unavailableMessage(wanted));
        return this.refresh().then(() => this.showError(null, this.unavailableMessage(wanted)));
      }

      this.setBusy(true);
      this.clearError(select);

      var headers = { 'Content-Type': 'application/json', Accept: 'application/json' };

      var payload = { id: match.id, quantity: quantity };
      var oldKey = item.dataset.ocdKey;
      var oldVariant = Number(item.dataset.ocdVariant);
      var oldProperties = {};
      var oldPlan = null;
      var metadata = item.querySelector('[data-ocd-line-data]');
      if (metadata) {
        try {
          var lineData = JSON.parse(metadata.textContent);
          oldProperties = Array.isArray(lineData.properties) ? Object.fromEntries(lineData.properties) : lineData.properties || {};
          payload.properties = oldProperties;
          oldPlan = lineData.selling_plan || null;
          if (oldPlan) payload.selling_plan = oldPlan;
        } catch (error) { this.setBusy(false); return; }
      }
      fetch(window.routes.cart_add_url + '.js', {
        method: 'POST', headers: headers,
        body: JSON.stringify({ items: [payload] }),
      })
        .then((response) => response.json().then((state) => {
          if (!response.ok || state.status) throw new Error(state.description || state.message);
          return fetch(window.routes.cart_url + '.js', { headers: { Accept: 'application/json' } });
        }))
        .then((response) => response.json().then((cart) => {
          if (!response.ok) throw new Error(window.cartStrings.error);
          var oldLine = cart.items.find((line) => {
            var properties = line.properties || {};
            var plan = line.selling_plan_allocation && line.selling_plan_allocation.selling_plan.id || null;
            return line.variant_id === oldVariant && plan === oldPlan &&
              Object.keys(properties).length === Object.keys(oldProperties).length &&
              Object.keys(oldProperties).every((key) => properties[key] === oldProperties[key]);
          });
          if (!oldLine) throw new Error(window.cartStrings.error);
          return fetch(window.routes.cart_change_url + '.js', {
            method: 'POST', headers: headers,
            body: JSON.stringify({ id: oldLine.key, quantity: 0,
              sections: this.getSectionsToRender().map((s) => s.id), sections_url: window.location.pathname }),
          });
        }))
        .then((response) => response.json().then((state) => {
          if (!response.ok || state.status) throw new Error(state.description || state.message);
          if (state.sections && state.sections[SECTION_ID]) this.renderFromState(state);
          else return this.refresh().then(() => this.publish(state));
        }))
        .catch((error) => this.refresh().then(() => this.showError(null, error.message || window.cartStrings.error)))
        .finally(() => this.setBusy(false));
    }

    async applyDiscount(form) {
      if (this.busy) return;
      var input = form.querySelector('[name="discount"]');
      var code = input.value.trim();
      if (!code) return;
      var codes = [];
      var data = this.querySelector('[data-ocd-discounts]');
      if (data) {
        try { codes = JSON.parse(data.textContent).filter((d) => d.type === 'discount_code').map((d) => d.title); }
        catch (error) { return; }
      }
      if (!codes.some((value) => value.toLowerCase() === code.toLowerCase())) codes.push(code);
      this.setBusy(true);
      try {
        var response = await fetch(window.routes.cart_update_url + '.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: codes.join(','), sections: this.getSectionsToRender().map((s) => s.id), sections_url: window.location.pathname }),
        });
        var state = await response.json();
        if (!response.ok || state.status) throw new Error('Discount request failed');
        if (state.sections && state.sections[SECTION_ID]) this.renderFromState(state);
        else { await this.refresh(); this.publish(state); }
        var updated = this.querySelector('[data-ocd-discounts]');
        var applied = updated && JSON.parse(updated.textContent).some((d) => d.type === 'discount_code' && d.title.toLowerCase() === code.toLowerCase());
        var status = this.querySelector('[data-ocd-discount-status]');
        if (status) { status.hidden = false; status.textContent = applied ? status.dataset.applied : status.dataset.invalid; }
      } catch (error) {
        var status = this.querySelector('[data-ocd-discount-status]');
        if (status) { status.hidden = false; status.textContent = status.dataset.error; }
      } finally { this.setBusy(false); }
    }

    unavailableMessage(wanted) {
      var text = window.variantStrings && window.variantStrings.unavailable_with_option;
      if (text) return text.replace('[value]', wanted.join(' / '));
      return 'That combination is not available.';
    }

    variantDataFor(item) {
      var script = item.querySelector('[data-ocd-variants]');
      if (!script) return null;
      try {
        return { variants: JSON.parse(script.textContent) };
      } catch (error) {
        return null;
      }
    }

    renderFromState(state) {
      this.refreshVersion++;
      if (state.sections) {
        this.getSectionsToRender().forEach((section) => {
          var html = state.sections[section.id];
          if (html) this.replaceSection(section, html);
        });
        this.setEmptyState(!!this.querySelector('.ocd__empty'));
      }
      this.publish(state);
      if (!this.busy) this.syncReservation();
    }

    refresh() {
      var version = ++this.refreshVersion;
      var url = new URL(window.location.href);
      url.searchParams.set('sections', this.getSectionsToRender().map((s) => s.id).join(','));
      url.searchParams.set('_ocd', String(Date.now()));
      return fetch(url.pathname + url.search, { cache: 'no-store' })
        .then((response) => { if (!response.ok) throw new Error('Section refresh failed'); return response.json(); })
        .then((sections) => {
          if (version !== this.refreshVersion) return;
          this.getSectionsToRender().forEach((section) => {
            if (sections[section.id]) this.replaceSection(section, sections[section.id]);
          });
          this.setEmptyState(!!this.querySelector('.ocd__empty'));
          if (!this.busy) this.syncReservation();
        })
        .catch(() => {});
    }

    publish(state) {
      if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'olivia-cart-drawer', cartData: state });
    }

    announce(state) {
      this.publish(state);
    }

    /* Reservation release is owned by the backend. The browser empties the
       Shopify cart on expiry, including after refresh or returning to a tab.
       Optional backend cart attribute: olivia_reservation_expires_at (ISO UTC).
       The timer requires a verified backend deadline and is disabled by default. */
    reservationStorage(action, key, value) {
      try {
        if (action === 'read') return window.localStorage.getItem(key);
        if (action === 'remove') window.localStorage.removeItem(key);
        else window.localStorage.setItem(key, String(value));
      } catch (error) { /* Storage can be unavailable in private browsers. */ }
      return null;
    }

    async syncReservation() {
      if (!this.querySelector('[data-ocd-reservation]') || this.busy || this.expiringReservation || !this.isConnected) return;
      var version = ++this.reservationSync;
      try {
        var response = await fetch(window.routes.cart_url + '.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
        if (!response.ok) return;
        var cart = await response.json();
        if (version === this.reservationSync && !this.busy && this.isConnected) this.acceptReservation(cart);
      } catch (error) { /* Keep the existing deadline during a temporary outage. */ }
    }

    acceptReservation(cart) {
      if (!this.querySelector('[data-ocd-reservation]')) { this.reservationDeadline = 0; return; }
      if (!cart || typeof cart.item_count !== 'number') return;
      var key = cart.token ? 'ocd-reservation-v1:' + window.location.host + ':' + cart.token : this.reservationKey;
      if (!cart.item_count) {
        if (key) this.reservationStorage('remove', key);
        if (this.reservationKey && key !== this.reservationKey) this.reservationStorage('remove', this.reservationKey);
        this.reservationDeadline = 0;
        this.reservationKey = key;
        this.reservationError = '';
        this.tickReservation();
        return;
      }
      var raw = cart.attributes && cart.attributes.olivia_reservation_expires_at;
      var backendDeadline = raw ? Date.parse(raw) : NaN;
      this.reservationKey = key;
      this.reservationToken = cart.token;
      this.reservationDeadline = Number.isFinite(backendDeadline) ? backendDeadline : 0;
      if (this.reservationDeadline > Date.now()) this.reservationError = '';
      if (key) this.reservationStorage('write', key, this.reservationDeadline);
      this.tickReservation();
    }

    tickReferenceTimer() {
      var time = this.querySelector('[data-ocd-visual-time]');
      if (!time) return;
      if (!this.referenceDeadline) {
        var key = 'ocd-reference-countdown:' + window.location.host;
        var stored = 0;
        try { stored = Number(window.sessionStorage.getItem(key)); } catch (error) {}
        this.referenceDeadline = stored || Date.now() + 297000;
        try { window.sessionStorage.setItem(key, String(this.referenceDeadline)); } catch (error) {}
      }
      var seconds = Math.max(0, Math.ceil((this.referenceDeadline - Date.now()) / 1000));
      time.textContent = String(Math.floor(seconds / 60)).padStart(2, '0') + ':' + String(seconds % 60).padStart(2, '0');
    }

    tickReservation() {
      var bar = this.querySelector('[data-ocd-reservation]');
      var remaining = this.reservationDeadline ? Math.max(0, Math.ceil((this.reservationDeadline - Date.now()) / 1000)) : 0;
      if (bar) {
        bar.hidden = !this.reservationDeadline;
        var time = bar.querySelector('[data-ocd-reservation-time]');
        if (time) time.textContent = String(Math.floor(remaining / 60)).padStart(2, '0') + ':' + String(remaining % 60).padStart(2, '0');
        var status = bar.querySelector('[data-ocd-reservation-status]');
        if (status) {
          status.textContent = this.reservationError || (!remaining && this.reservationDeadline ? 'Reservation expired. Clearing your cart…' : '');
          status.hidden = !status.textContent;
        }
      }
      var checkout = this.querySelector('.ocd__checkout');
      if (checkout) checkout.disabled = this.busy || (!!this.reservationDeadline && !remaining);
      if (this.reservationDeadline && !remaining && !this.busy && !this.expiringReservation && Date.now() >= (this.reservationRetryAt || 0)) this.expireReservation();
    }

    async expireReservation() {
      this.expiringReservation = true;
      this.setBusy(true);
      try {
        // Re-read before clearing: another tab or the backend may have renewed
        // the reservation. Never clear against a stale cart identity/deadline.
        var check = await fetch(window.routes.cart_url + '.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
        if (!check.ok) throw new Error('Cart check failed');
        var current = await check.json();
        var changedCart = this.reservationToken && current.token !== this.reservationToken;
        this.acceptReservation(current);
        if (changedCart || !current.item_count || !this.reservationDeadline || Date.now() < this.reservationDeadline) return;
        var response = await fetch(window.routes.cart_url.replace(/\/$/, '') + '/clear.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ sections: this.getSectionsToRender().map((s) => s.id), sections_url: window.location.pathname }),
        });
        var state = await response.json();
        // A rendering error can accompany a successful mutation. Confirm the
        // actual cart before deciding whether the clear needs retrying.
        if (!response.ok || state.item_count !== 0) {
          var verify = await fetch(window.routes.cart_url + '.js', { cache: 'no-store' });
          if (!verify.ok) throw new Error('Cart clear failed');
          state = await verify.json();
          if (state.item_count !== 0) throw new Error('Cart clear failed');
        }
        this.acceptReservation(state);
        this.renderFromState(state);
        if (!state.sections || !state.sections[SECTION_ID]) await this.refresh();
        if (!state.sections || !state.sections['cart-icon-bubble']) {
          var bubble = await fetch(window.location.pathname + '?section_id=cart-icon-bubble');
          if (!bubble.ok) throw new Error('Cart badge refresh failed');
          this.replaceSection(this.getSectionsToRender()[1], await bubble.text());
        }
        this.reservationRetryAt = 0;
      } catch (error) {
        this.reservationError = 'Unable to clear your cart. Please check your connection. Retrying…';
        this.reservationRetryAt = Date.now() + 10000;
      } finally {
        this.expiringReservation = false;
        this.setBusy(false);
        this.tickReservation();
      }
    }

    showError(origin, message) {
      if (!message) return;
      var item = origin && origin.closest ? origin.closest('.ocd__item') : null;
      var target = item ? item.querySelector('[data-ocd-item-error]') : null;
      if (!target) target = this.querySelector('[data-ocd-error]');
      if (!target) return;
      target.textContent = message;
      target.hidden = false;
    }

    clearError(origin) {
      var item = origin && origin.closest ? origin.closest('.ocd__item') : null;
      var target = item ? item.querySelector('[data-ocd-item-error]') : null;
      var global = this.querySelector('[data-ocd-error]');
      if (global) { global.textContent = ''; global.hidden = true; }
      if (target) {
        target.textContent = '';
        target.hidden = true;
      }
    }
  }

  customElements.define('cart-drawer', OliviaCartDrawer);

  /* Theme editor: selecting the "Olivia cart drawer" section opens the
     drawer, so its settings can be seen while they are edited. It stays open
     while the section re-renders after each change and closes when another
     section is selected. */
  if (window.Shopify && window.Shopify.designMode) {
    var editingDrawer = false;
    var isDrawerSection = function (event) {
      return !!(event.detail && event.detail.sectionId === SECTION_ID);
    };
    var editorDrawer = function () {
      var drawer = document.querySelector('cart-drawer');
      return drawer && typeof drawer.open === 'function' ? drawer : null;
    };
    document.addEventListener('shopify:section:select', function (event) {
      var drawer = editorDrawer();
      if (isDrawerSection(event)) {
        editingDrawer = true;
        if (drawer) drawer.open();
      } else if (editingDrawer) {
        editingDrawer = false;
        if (drawer) drawer.close();
      }
    });
    // A Template block of the drawer is selected: open the drawer too.
    document.addEventListener('shopify:block:select', function (event) {
      if (!isDrawerSection(event)) return;
      editingDrawer = true;
      var drawer = editorDrawer();
      if (drawer) drawer.open();
    });
    document.addEventListener('shopify:section:deselect', function (event) {
      if (!isDrawerSection(event)) return;
      editingDrawer = false;
      var drawer = editorDrawer();
      if (drawer) drawer.close();
    });
    document.addEventListener('shopify:section:load', function (event) {
      if (!isDrawerSection(event) || !editingDrawer) return;
      var drawer = editorDrawer();
      if (drawer) drawer.open();
    });
  }

  /* The header cart link should open the drawer instead of loading /cart. */
  document.addEventListener('click', function (event) {
    var link = event.target.closest('a[href$="/cart"], a[href*="/cart?"], #cart-icon-bubble a');
    if (!link) return;
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.open !== 'function') return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.which === 2) return;
    event.preventDefault();
    drawer.refresh().then(function () {
      drawer.open(link);
    });
  });
})();
